<template>
  <div v-if="snapshot" class="dimension-property-section">
    <div class="property-row">
      <span class="property-label">名称</span>
      <n-text class="property-static">{{ snapshot.name }}</n-text>
    </div>

    <template v-if="snapshot.solveState === 'dangling'">
      <div class="property-row">
        <span class="property-label">状态</span>
        <n-text class="property-dangling">悬空（引用已丢失）</n-text>
      </div>
      <div class="property-row">
        <span class="property-label">修复</span>
        <n-button size="tiny" :type="reattachSession ? 'warning' : 'primary'" @click="toggleReattachSession">
          {{ reattachSession
            ? `点选新引用（${reattachSession.picks.length}/${reattachSession.required}），再按一次取消`
            : '重新附着' }}
        </n-button>
      </div>
    </template>

    <div class="property-row">
      <span class="property-label">数值</span>
      <n-input
        :value="valueText"
        size="small"
        :placeholder="isAngle ? '如 45 或 90/2' : '如 60 或 30mm+2in'"
        :disabled="snapshot.role === 'driven'"
        @update:value="handleValueTextUpdate"
        @keyup.enter="applyValue"
        @focus="prepareValueEdit"
        @blur="applyValue"
      >
        <template #suffix>{{ isAngle ? '°' : 'mm' }}</template>
      </n-input>
    </div>

    <div class="property-row">
      <span class="property-label">从动</span>
      <n-switch
        size="small"
        :value="snapshot.role === 'driven'"
        @update:value="applyRole"
      />
    </div>

    <div v-if="isRadial" class="property-row">
      <span class="property-label">显示样式</span>
      <n-radio-group
        size="small"
        :value="displayStyle"
        @update:value="applyDisplay({ displayStyle: $event })"
      >
        <n-radio-button value="radial">R 径向</n-radio-button>
        <n-radio-button value="diametric">⌀ 直径</n-radio-button>
        <n-radio-button value="linear">线性</n-radio-button>
      </n-radio-group>
    </div>

    <div class="property-row">
      <span class="property-label">精度</span>
      <n-select
        size="small"
        class="property-select"
        :value="precision"
        :options="PRECISION_OPTIONS"
        @update:value="applyDisplay({ precision: $event })"
      />
    </div>

    <div class="property-row">
      <span class="property-label">单位</span>
      <n-select
        size="small"
        class="property-select"
        :value="snapshot.display.unitOverride ?? DEFAULT_UNIT_VALUE"
        :options="unitOptions"
        @update:value="applyUnitOverride"
      />
    </div>

    <div class="property-row">
      <span class="property-label">公差</span>
      <n-select
        size="small"
        class="property-select"
        :value="toleranceMode"
        :options="TOLERANCE_MODE_OPTIONS"
        @update:value="applyToleranceMode"
      />
    </div>
    <template v-if="toleranceMode !== 'none'">
      <div class="property-row">
        <span class="property-label">{{ toleranceMode === 'symmetric' ? '±' : '+' }}</span>
        <n-input-number
          size="small"
          class="property-select"
          :value="tolerancePlusDisplay"
          :min="0"
          :show-button="false"
          @update:value="applyToleranceValue('plus', $event)"
        >
          <template #suffix>{{ unitSuffix }}</template>
        </n-input-number>
      </div>
      <div v-if="toleranceMode === 'bilateral'" class="property-row">
        <span class="property-label">-</span>
        <n-input-number
          size="small"
          class="property-select"
          :value="toleranceMinusDisplay"
          :min="0"
          :show-button="false"
          @update:value="applyToleranceValue('minus', $event)"
        >
          <template #suffix>{{ unitSuffix }}</template>
        </n-input-number>
      </div>
    </template>

    <div class="property-row">
      <span class="property-label">覆盖值</span>
      <n-checkbox
        size="small"
        :checked="overrideEnabled"
        @update:checked="toggleOverride"
      />
      <n-input
        v-if="overrideEnabled"
        v-model:value="overrideText"
        size="small"
        placeholder="覆盖显示文本"
        @keydown.enter.prevent="applyOverrideText"
        @blur="applyOverrideText"
      />
    </div>

    <div class="property-row">
      <span class="property-label">隐藏界线</span>
      <n-checkbox
        size="small"
        :checked="snapshot.display.extensionLinesHidden === true"
        @update:checked="applyDisplay({ extensionLinesHidden: $event })"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 草图尺寸属性 section（阶段 7，DISP-001/002 + VAL-004 完整显示属性）。
 *
 * 设计说明：
 * 1. 自拉数据模式：可见性由 `context.sketchDimensionSelection` 判据，属性数据由本组件
 *    直接从 `context.renderer` 的尺寸仓库读取，宿主 context 不再扩散尺寸字段；
 * 2. 所有显示属性编辑走 `viewer.updateSketchDimensionDisplay`（每次一个 Model step，
 *    不触碰求解参数）；数值走 `modifySketchDimensionValue`（表达式解析 + 负值方向翻转）；
 * 3. 仓库 observe 订阅保证 undo/redo 与外部修改时面板跟随刷新；
 * 4. UI 输入用显示单位（mm/°），核心单位换算在本组件边界完成。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { NButton, NCheckbox, NInput, NInputNumber, NRadioButton, NRadioGroup, NSelect, NSwitch, NText, useMessage } from 'naive-ui';
import {
  convertMmToM,
  convertMToMm,
  parseSketchDimensionValueInput,
  projectSmartDimensionPickItem,
} from '@puppycad/cad-core';
import type { SketchDimensionSnapshot, Viewer } from '@puppycad/cad-core';
import type { PropertyManagerContext } from '@/core';
import type * as THREE from 'three';

const props = defineProps<{
  context: PropertyManagerContext;
}>();

const message = useMessage();

/** 精度选项（0-8 位小数）。 */
const PRECISION_OPTIONS = Array.from({ length: 9 }, (_, index) => ({ label: `${index} 位小数`, value: index }));

/** 单位下拉的"文档默认"哨兵值。 */
const DEFAULT_UNIT_VALUE = '__default__';

/** 公差模式选项。 */
const TOLERANCE_MODE_OPTIONS = [
  { label: '无', value: 'none' },
  { label: '对称 ±', value: 'symmetric' },
  { label: '双侧 +/-', value: 'bilateral' },
];

/** 面板刷新信号（仓库事件驱动）。 */
const refreshSignal = ref(0);
/** 数值输入文本（表达式）。 */
const valueText = ref('');
/** 覆盖值输入文本。 */
const overrideText = ref('');
/** 尺寸值提交互斥标记，避免 Enter 后紧接失焦重复开启求解与历史事务。 */
let valueCommitPending = false;

/** 当前选中尺寸键。 */
const selection = computed(() => props.context.sketchDimensionSelection ?? null);

/** 读取 viewer 句柄。 */
function getViewer(): Viewer | null {
  return (props.context.renderer as Viewer | null) ?? null;
}

/** 当前尺寸快照（选中变化/仓库事件驱动重算）。 */
const snapshot = computed<SketchDimensionSnapshot | null>(() => {
  void refreshSignal.value;
  const target = selection.value;
  if (!target) {
    return null;
  }
  return getViewer()?.getSketchDimensionRepository()
    ?.get(target.sketchId, target.dimensionId)?.toSnapshot() ?? null;
});

/** 仓库订阅（undo/redo 与外部修改跟随）。 */
let disposeRepositorySubscription: (() => void) | null = null;

watch(
  () => [selection.value?.sketchId ?? null, props.context.renderer] as const,
  () => {
    disposeRepositorySubscription?.();
    disposeRepositorySubscription = getViewer()?.getSketchDimensionRepository()?.observe(() => {
      refreshSignal.value += 1;
    }) ?? null;
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  disposeRepositorySubscription?.();
  disposeRepositorySubscription = null;
});

const isAngle = computed(() => snapshot.value?.parameter.quantityKind === 'angle');
const isRadial = computed(() => snapshot.value?.dimensionKind === 'radius' || snapshot.value?.dimensionKind === 'diameter');
const precision = computed(() => {
  const value = snapshot.value?.display.precision;
  return typeof value === 'number' ? Math.min(Math.max(Math.trunc(value), 0), 8) : 2;
});
const displayStyle = computed(() => {
  const current = snapshot.value;
  if (!current) {
    return 'linear';
  }
  return current.display.displayStyle
    ?? (current.dimensionKind === 'diameter' ? 'diametric' : current.dimensionKind === 'radius' ? 'radial' : 'linear');
});

/** 单位覆盖选项（量纲相关）。 */
const unitOptions = computed(() => (isAngle.value
  ? [
      { label: '默认（°）', value: DEFAULT_UNIT_VALUE },
      { label: '度 °', value: 'deg' },
      { label: '弧度 rad', value: 'rad' },
    ]
  : [
      { label: '默认（mm）', value: DEFAULT_UNIT_VALUE },
      { label: '毫米 mm', value: 'mm' },
      { label: '厘米 cm', value: 'cm' },
      { label: '米 m', value: 'm' },
      { label: '英寸 in', value: 'in' },
    ]));

/** 公差输入后缀（跟随单位覆盖）。 */
const unitSuffix = computed(() => {
  if (isAngle.value) {
    return snapshot.value?.display.unitOverride === 'rad' ? 'rad' : '°';
  }
  return snapshot.value?.display.unitOverride ?? 'mm';
});

/** 当前公差模式。 */
const toleranceMode = computed(() => {
  const display = snapshot.value?.display;
  if (display?.tolerancePlus === undefined && display?.toleranceMinus === undefined) {
    return 'none';
  }
  return (display?.tolerancePlus ?? 0) === (display?.toleranceMinus ?? 0) ? 'symmetric' : 'bilateral';
});

/** 核心单位 → 显示单位（跟随单位覆盖，用于公差展示）。 */
function coreToDisplay(coreValue: number): number {
  if (isAngle.value) {
    return snapshot.value?.display.unitOverride === 'rad'
      ? Number(coreValue.toFixed(6))
      : Number(((coreValue * 180) / Math.PI).toFixed(6));
  }
  const millimeters = convertMToMm(coreValue);
  const unit = snapshot.value?.display.unitOverride;
  const factor = unit === 'cm' ? 10 : unit === 'm' ? 1000 : unit === 'in' ? 25.4 : 1;
  return Number((millimeters / factor).toFixed(6));
}

/** 显示单位 → 核心单位（公差输入）。 */
function displayToCore(displayValue: number): number {
  if (isAngle.value) {
    return snapshot.value?.display.unitOverride === 'rad'
      ? displayValue
      : (displayValue * Math.PI) / 180;
  }
  const unit = snapshot.value?.display.unitOverride;
  const factor = unit === 'cm' ? 10 : unit === 'm' ? 1000 : unit === 'in' ? 25.4 : 1;
  return convertMmToM(displayValue * factor);
}

const tolerancePlusDisplay = computed(() => coreToDisplay(snapshot.value?.display.tolerancePlus ?? 0));
const toleranceMinusDisplay = computed(() => coreToDisplay(snapshot.value?.display.toleranceMinus ?? 0));

const overrideEnabled = computed(() => {
  const overrideValue = snapshot.value?.display.overrideValue;
  return typeof overrideValue === 'string' && overrideValue.length > 0;
});

//------ 悬空重新附着（DANG-002 I 级）开始------
/** 重附着会话：依次点选与原引用等量的新引用。 */
const reattachSession = ref<{
  required: number;
  picks: { entityId: string; topologyRole: string; pointId?: string }[];
} | null>(null);

/** 开关重附着会话。 */
function toggleReattachSession(): void {
  if (reattachSession.value) {
    reattachSession.value = null;
    return;
  }
  const current = snapshot.value;
  if (!current) {
    return;
  }
  reattachSession.value = { required: current.references.length, picks: [] };
  message.info(`请在视口依次点选 ${current.references.length} 个新引用实体`);
}

/**
 * 会话期间消费草图选区对象：映射为稳定引用并凑满后提交。
 *
 * 说明：`context.selectedObject` 是设计树选中，草图实体点选的真相源是
 * 选择中心（`entitySelectionManager`）；context 每次宿主渲染都是新对象，
 * 以其引用变化作为"选择可能变化"的触发信号。
 */
watch(
  () => props.context,
  async () => {
    const session = reattachSession.value;
    const target = selection.value;
    const selected = getViewer()?.entitySelectionManager?.getSelectedObjects?.()[0] ?? null;
    if (!session || !target || !selected) {
      return;
    }
    const pick = projectSmartDimensionPickItem(selected as THREE.Object3D, session.picks.length);
    if (!pick) {
      return;
    }
    // 同一对象重复选择忽略。
    if (session.picks.some((item) => item.entityId === pick.entityId && item.pointId === pick.pointId)) {
      return;
    }
    session.picks.push({
      entityId: pick.entityId,
      topologyRole: pick.topologyRole ?? 'entity',
      ...(pick.pointId ? { pointId: pick.pointId } : {}),
    });
    if (session.picks.length < session.required) {
      return;
    }
    const references = session.picks.map((item, index) => ({
      entityId: item.entityId,
      topologyRole: item.topologyRole as never,
      ...(item.pointId ? { pointId: item.pointId } : {}),
      selectionOrder: index,
    }));
    reattachSession.value = null;
    try {
      await getViewer()?.reattachSketchDimension(target.sketchId, target.dimensionId, references);
      message.success('尺寸已重新附着');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '重新附着失败');
    }
  },
);

/** 选中尺寸切换时终止未完成的重附着会话。 */
watch(selection, () => {
  reattachSession.value = null;
});
//------ 悬空重新附着 结束------

/** 数值输入回填（选中/快照变化时）。 */
watch(
  snapshot,
  (current) => {
    if (!current) {
      valueText.value = '';
      overrideText.value = '';
      return;
    }
    const coreValue = current.role === 'driven' && current.measuredValue !== null
      ? current.measuredValue
      : current.parameter.nominalValue;
    valueText.value = current.parameter.quantityKind === 'angle'
      ? String(Number(((coreValue * 180) / Math.PI).toFixed(3)))
      : String(Number(convertMToMm(coreValue).toFixed(3)));
    overrideText.value = current.display.overrideValue ?? '';
  },
  { immediate: true },
);

/**
 * 进入已存在尺寸的属性编辑前结束智能尺寸创建命令。
 *
 * 创建命令持有自己的选择与预演基线；若继续保持激活，输入框失焦时的画布点击会先推进
 * 创建会话并恢复旧基线。属性编辑属于独立操作，聚焦时退出创建态可避免两套会话争用。
 */
function prepareValueEdit(): void {
  getViewer()?.cancelSketchSmartDimensionMode();
}

/**
 * 接收尺寸文本更新并只保留本地草稿。
 *
 * 与线条属性的长度输入一致，输入阶段禁止修改尺寸仓库或触发参数求解；
 * 只有 Enter 或失焦事件才调用 `applyValue()` 提交正式值。
 *
 * @param nextValue 最新输入文本。
 */
function handleValueTextUpdate(nextValue: string): void {
  valueText.value = nextValue;
}

/** 应用数值（表达式解析 → 求解重驱动；负值翻转方向）。 */
async function applyValue(): Promise<void> {
  const target = selection.value;
  const current = snapshot.value;
  const viewer = getViewer();
  if (!target || !current || !viewer || current.role === 'driven') {
    return;
  }
  await commitValueText(valueText.value, target, current, viewer);
}

/**
 * 使用输入时捕获的稳定尺寸目标提交数值。
 *
 * @param inputText 待解析的尺寸表达式。
 * @param target 输入开始时选中的尺寸键。
 * @param current 输入开始时的尺寸快照。
 * @param viewer 尺寸所属 Viewer。
 */
async function commitValueText(
  inputText: string,
  target: NonNullable<PropertyManagerContext['sketchDimensionSelection']>,
  current: SketchDimensionSnapshot,
  viewer: Viewer,
): Promise<void> {
  if (valueCommitPending) {
    return;
  }
  const parsed = parseSketchDimensionValueInput(inputText, current.parameter.quantityKind);
  if (parsed.ok === false) {
    message.error(parsed.message);
    resetValueText();
    return;
  }
  if (!parsed.negated && Math.abs(parsed.coreValue - current.parameter.nominalValue) <= 1e-12) {
    // Enter 提交成功后可能紧接一次 blur；相同值只回填规范显示，不生成重复事务。
    resetValueText();
    return;
  }
  valueCommitPending = true;
  try {
    await viewer.modifySketchDimensionValue(
      target.sketchId,
      target.dimensionId,
      parsed.coreValue,
      parsed.negated ? { flipDirectionSign: true } : undefined,
    );
    // 仓库事件已同步刷新快照，此处统一回填换算和舍入后的正式显示值。
    resetValueText();
  } catch (error) {
    message.error(error instanceof Error ? error.message : '修改尺寸值失败');
    resetValueText();
  } finally {
    valueCommitPending = false;
  }
}

/** 从尺寸仓库快照回填当前正式值。 */
function resetValueText(): void {
  const current = snapshot.value;
  if (!current) {
    return;
  }
  const coreValue = current.role === 'driven' && current.measuredValue !== null
    ? current.measuredValue
    : current.parameter.nominalValue;
  valueText.value = current.parameter.quantityKind === 'angle'
    ? String(Number(((coreValue * 180) / Math.PI).toFixed(3)))
    : String(Number(convertMToMm(coreValue).toFixed(3)));
}

/** 切换驱动/从动。 */
async function applyRole(driven: boolean): Promise<void> {
  const target = selection.value;
  if (!target) {
    return;
  }
  try {
    await getViewer()?.changeSketchDimensionRole(target.sketchId, target.dimensionId, driven ? 'driven' : 'driving');
  } catch (error) {
    message.error(error instanceof Error ? error.message : '切换驱动角色失败');
  }
}

/** 应用显示属性补丁（浅合并，一个 Model step）。 */
async function applyDisplay(display: Record<string, unknown>): Promise<void> {
  const target = selection.value;
  if (!target) {
    return;
  }
  try {
    await getViewer()?.updateSketchDimensionDisplay(target.sketchId, target.dimensionId, display);
  } catch (error) {
    message.error(error instanceof Error ? error.message : '修改显示属性失败');
  }
}

/** 应用单位覆盖（默认哨兵 → undefined 清除）。 */
async function applyUnitOverride(value: string): Promise<void> {
  await applyDisplay({ unitOverride: value === DEFAULT_UNIT_VALUE ? undefined : value });
}

/** 切换公差模式。 */
async function applyToleranceMode(mode: string): Promise<void> {
  if (mode === 'none') {
    await applyDisplay({ tolerancePlus: undefined, toleranceMinus: undefined });
    return;
  }
  const plus = snapshot.value?.display.tolerancePlus ?? 0;
  const minus = mode === 'symmetric' ? plus : (snapshot.value?.display.toleranceMinus ?? 0);
  await applyDisplay({ tolerancePlus: plus, toleranceMinus: minus });
}

/** 修改公差值（对称模式双侧同步）。 */
async function applyToleranceValue(side: 'plus' | 'minus', displayValue: number | null): Promise<void> {
  if (typeof displayValue !== 'number' || !Number.isFinite(displayValue) || displayValue < 0) {
    return;
  }
  const coreValue = displayToCore(displayValue);
  if (toleranceMode.value === 'symmetric') {
    await applyDisplay({ tolerancePlus: coreValue, toleranceMinus: coreValue });
    return;
  }
  await applyDisplay(side === 'plus' ? { tolerancePlus: coreValue } : { toleranceMinus: coreValue });
}

/** 开关覆盖值（关闭时清除文本，DISP-002）。 */
async function toggleOverride(enabled: boolean): Promise<void> {
  if (!enabled) {
    await applyDisplay({ overrideValue: null });
    return;
  }
  overrideText.value = overrideText.value || '**';
  await applyDisplay({ overrideValue: overrideText.value });
}

/** 提交覆盖文本。 */
async function applyOverrideText(): Promise<void> {
  if (!overrideEnabled.value) {
    return;
  }
  const text = overrideText.value.trim();
  if (text.length === 0) {
    await applyDisplay({ overrideValue: null });
    return;
  }
  if (text !== snapshot.value?.display.overrideValue) {
    await applyDisplay({ overrideValue: text });
  }
}
</script>

<style scoped>
.dimension-property-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.property-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.property-label {
  flex: 0 0 56px;
  font-size: 12px;
  color: #9a9ea8;
}

.property-static {
  font-size: 12px;
}

.property-dangling {
  font-size: 12px;
  color: #8b5a2b;
}

.property-select {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
